import { Box, Tooltip, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { NO_DATA_TEXT } from "../../shared/consumption/utils/format";

interface ConsumptionMetricProps {
	label: string;
	value: string | null;
	unit?: string;
	secondary?: ReactNode;
	lead?: boolean;
	tooltip?: string;
	testId?: string;
}

export const ConsumptionMetric = ({
	label,
	value,
	unit,
	secondary,
	lead = false,
	tooltip,
	testId,
}: ConsumptionMetricProps) => {
	const metric = (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 0.25,
				justifyContent: "flex-start",
				minWidth: 128,
			}}
		>
			<Typography
				component="div"
				sx={{
					color: "#48525C",
					fontSize: "0.75rem",
					fontWeight: 500,
					lineHeight: 1.35,
					minHeight: "2.7em",
				}}
			>
				{label}
			</Typography>
			<Box
				sx={{
					alignItems: "baseline",
					display: "flex",
					gap: 0.5,
					minWidth: 0,
					whiteSpace: "nowrap",
				}}
			>
				<Typography
					component="span"
					data-test-id={testId}
					aria-label={value === null ? "No data" : undefined}
					sx={{
						fontSize: "1.75rem",
						fontVariantNumeric: "tabular-nums",
						fontWeight: 600,
						lineHeight: 1,
						...(lead && { fontSize: "2rem" }),
						color: value === null ? "#5B6670" : "text.primary",
					}}
				>
					{value ?? NO_DATA_TEXT}
				</Typography>
				{value !== null && unit && (
					<Typography
						component="span"
						sx={{
							color: "#5B6670",
							fontSize: "0.875rem",
							fontWeight: 400,
							lineHeight: 1.2,
						}}
					>
						{unit}
					</Typography>
				)}
			</Box>
			{secondary && (
				<Typography
					component="div"
					sx={{
						color: "#48525C",
						fontSize: "0.75rem",
						fontWeight: 400,
						lineHeight: 1.4,
					}}
				>
					{secondary}
				</Typography>
			)}
		</Box>
	);

	if (!tooltip) return metric;

	return (
		<Tooltip describeChild title={tooltip}>
			<Box
				aria-label={label}
				tabIndex={0}
				sx={{
					minWidth: 0,
					outline: "none",
					"&:focus-visible": {
						borderRadius: 1,
						outline: "2px solid currentColor",
						outlineOffset: 2,
					},
				}}
			>
				{metric}
			</Box>
		</Tooltip>
	);
};
